import React, { useState, useEffect } from 'react';

function FoodPreference({ onNavigate, userInfo }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [userData, setUserData] = useState(null);
    const [hasExistingData, setHasExistingData] = useState(false);
    const [mealPreference, setMealPreference] = useState('veg');
    const [selectedMeals, setSelectedMeals] = useState({
        monday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        tuesday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        wednesday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        thursday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        friday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        saturday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' },
        sunday: { breakfast: 'veg', lunch: 'veg', dinner: 'veg' }
    });
    const [stats, setStats] = useState({
        currentPlan: 'Premium',
        defaultPreference: 'Veg',
        totalMeals: 21,
        vegMeals: 21,
        nonVegMeals: 0
    });
    
    // New state for dynamic menu
    const [menuItems, setMenuItems] = useState({
        veg: {
            breakfast: [],
            lunch: [],
            dinner: []
        },
        nonveg: {
            breakfast: [],
            lunch: [],
            dinner: []
        }
    });
    const [menuLoading, setMenuLoading] = useState(true);

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    // Fetch menu items from backend
    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                setMenuLoading(true);
                
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await fetch(
                    `${API_BASE_URL}/student/menu`,
                    { method: 'GET', headers }
                );

                if (response.ok) {
                    const menuData = await response.json();
                    if (menuData.success && menuData.menu) {
                        setMenuItems(menuData.menu);
                    }
                }
            } catch (err) {
                console.error('Error fetching menu items:', err);
                // Keep default empty menu if fetch fails
            } finally {
                setMenuLoading(false);
            }
        };

        fetchMenuItems();
    }, [API_BASE_URL]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = userInfo?.userId || userInfo?.id;
                
                if (!userId) {
                    throw new Error('User ID not found');
                }

                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch user profile data
                const profileResponse = await fetch(
                    `${API_BASE_URL}/student/profile/${userId}`,
                    { method: 'GET', headers }
                );

                if (profileResponse.ok) {
                    const profileData = await profileResponse.json();
                    if (profileData.success && profileData.data) {
                        setUserData(profileData.data);
                    }
                }

                // Fetch food preferences - ONLY show real data
                const prefsResponse = await fetch(
                    `${API_BASE_URL}/student/food-preferences/${userId}`,
                    { method: 'GET', headers }
                );

                if (prefsResponse.ok) {
                    const prefsData = await prefsResponse.json();

                    // Only set data if it actually exists in the database
                    if (prefsData.success && prefsData.data) {
                        setHasExistingData(true);
                        setMealPreference(prefsData.data.defaultPreference || 'veg');
                        setSelectedMeals(prefsData.data.weeklySelections);
                    } else {
                        // No data in database - keep initial defaults
                        setHasExistingData(false);
                    }
                }

                // Fetch statistics - ONLY show if data exists
                const statsResponse = await fetch(
                    `${API_BASE_URL}/student/food-preferences-stats/${userId}`,
                    { method: 'GET', headers }
                );

                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    if (statsData.success && statsData.stats) {
                        setStats(statsData.stats);
                    }
                }

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [userInfo, API_BASE_URL]);

    const weekDays = [
        { key: 'monday', name: 'Monday', icon: '📅' },
        { key: 'tuesday', name: 'Tuesday', icon: '📅' },
        { key: 'wednesday', name: 'Wednesday', icon: '📅' },
        { key: 'thursday', name: 'Thursday', icon: '📅' },
        { key: 'friday', name: 'Friday', icon: '📅' },
        { key: 'saturday', name: 'Saturday', icon: '📅' },
        { key: 'sunday', name: 'Sunday', icon: '📅' }
    ];

    const handleMealToggle = (day, mealTime, preference) => {
        setSelectedMeals(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [mealTime]: preference
            }
        }));
    };

    const handleSavePreferences = async () => {
        try {
            setSaving(true);
            setError(null);

            const userId = userInfo?.userId || userInfo?.id;
            
            if (!userId) {
                throw new Error('User ID not found');
            }

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(
                `${API_BASE_URL}/student/food-preferences/${userId}`,
                {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({
                        defaultPreference: mealPreference,
                        mealPlan: 'premium',
                        weeklySelections: selectedMeals,
                        specialDietNotes: null
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to save preferences');
            }

            const data = await response.json();

            if (data.success) {
                setHasExistingData(true);
                alert('Food preferences saved successfully!');
                
                // Refresh stats
                const statsResponse = await fetch(
                    `${API_BASE_URL}/student/food-preferences-stats/${userId}`,
                    { method: 'GET', headers }
                );

                if (statsResponse.ok) {
                    const statsData = await statsResponse.json();
                    if (statsData.success && statsData.stats) {
                        setStats(statsData.stats);
                    }
                }
            } else {
                throw new Error(data.message || 'Failed to save preferences');
            }

        } catch (err) {
            console.error('Error saving preferences:', err);
            alert(`Error: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    function MenuItem({ icon, label, active = false, route }) {
        return (
            <div
                className={`menu-item ${active ? 'menu-item-active' : ''}`}
                onClick={() => {
                    if (route && onNavigate) {
                        onNavigate(route);
                    }
                    setSidebarOpen(false);
                }}
            >
                <span className="menu-icon">{icon}</span>
                <span className="menu-label">{label}</span>
                <div className="menu-glow"></div>
            </div>
        );
    }

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    const displayName = userData?.fullName || userInfo?.name || 'Student';
    const displayInitial = displayName.charAt(0).toUpperCase();

    if (loading || menuLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ 
                    color: '#2b6777', 
                    fontSize: '18px', 
                    fontWeight: '600' 
                }}>Loading Food Preferences...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                }

                .no-data-banner {
                    background: linear-gradient(135deg, rgba(255,193,7,0.1) 0%, rgba(255,152,0,0.1) 100%);
                    border: 2px solid rgba(255,193,7,0.3);
                    border-radius: 12px;
                    padding: 16px 20px;
                    margin-bottom: 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    animation: slideIn 0.6s ease-out;
                }

                .no-data-icon {
                    font-size: 24px;
                    flex-shrink: 0;
                }

                .no-data-content {
                    flex: 1;
                }

                .no-data-title {
                    font-weight: 700;
                    color: #f57c00;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .no-data-text {
                    font-size: 13px;
                    color: rgba(245,124,0,0.8);
                }

                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                .preference-toggle-section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 16px;
                }

                .toggle-label {
                    font-size: 16px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .toggle-buttons {
                    display: flex;
                    gap: 12px;
                }

                .toggle-btn {
                    padding: 12px 24px;
                    border: 2px solid rgba(82,171,152,0.25);
                    border-radius: 10px;
                    background: rgba(82,171,152,0.05);
                    color: #2b6777;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .toggle-btn:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.4);
                    transform: translateY(-2px);
                }

                .toggle-btn.active {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-color: transparent;
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                }

                .section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .section-title-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .save-btn {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .save-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.4);
                }

                .save-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                .menu-display {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 20px;
                    margin-bottom: 24px;
                }

                .meal-section {
                    background: rgba(82,171,152,0.05);
                    border-radius: 14px;
                    padding: 20px;
                    border: 1.5px solid rgba(82,171,152,0.2);
                }

                .meal-header {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 16px;
                }

                .meal-icon {
                    font-size: 28px;
                }

                .meal-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .menu-items {
                    display: grid;
                    gap: 12px;
                }

                .menu-item-card {
                    background: rgba(255,255,255,0.9);
                    border-radius: 12px;
                    padding: 14px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                    transition: all 0.3s;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .menu-item-card:hover {
                    background: rgba(255,255,255,1);
                    border-color: rgba(82,171,152,0.3);
                    transform: translateX(4px);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.12);
                }

                .menu-item-icon {
                    font-size: 28px;
                }

                .menu-item-info {
                    flex: 1;
                }

                .menu-item-name {
                    font-size: 14px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .menu-item-desc {
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                }

                .empty-menu {
                    text-align: center;
                    padding: 40px 20px;
                    color: rgba(43,103,119,0.5);
                    font-size: 14px;
                    font-weight: 600;
                }

                .weekly-selection {
                    display: grid;
                    gap: 16px;
                }

                .day-selection-card {
                    background: rgba(82,171,152,0.05);
                    border-radius: 14px;
                    padding: 20px;
                    border: 1.5px solid rgba(82,171,152,0.2);
                    transition: all 0.3s;
                }

                .day-selection-card:hover {
                    background: rgba(82,171,152,0.08);
                    border-color: rgba(82,171,152,0.3);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.12);
                }

                .day-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 16px;
                    padding-bottom: 14px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                }

                .day-icon {
                    width: 44px;
                    height: 44px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: rgba(255,255,255,0.9);
                    border: 1.5px solid rgba(82,171,152,0.25);
                }

                .day-name {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .meals-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 14px;
                }

                .meal-selection {
                    background: rgba(255,255,255,0.9);
                    border-radius: 12px;
                    padding: 16px;
                    border: 1.5px solid rgba(82,171,152,0.15);
                }

                .meal-time-label {
                    font-size: 12px;
                    font-weight: 800;
                    color: rgba(43,103,119,0.6);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 10px;
                }

                .meal-options {
                    display: flex;
                    gap: 8px;
                }

                .meal-option-btn {
                    flex: 1;
                    padding: 10px 14px;
                    border: 2px solid rgba(82,171,152,0.25);
                    border-radius: 8px;
                    background: rgba(82,171,152,0.05);
                    color: #2b6777;
                    font-size: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .meal-option-btn:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.4);
                }

                .meal-option-btn.active {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border-color: transparent;
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                }

                .meal-option-btn.veg.active {
                    background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
                }

                .meal-option-btn.nonveg.active {
                    background: linear-gradient(135deg, #f44336 0%, #ef5350 100%);
                }

                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .section {
                        padding: 32px 28px;
                    }

                    .section-header {
                        gap: 16px;
                        margin-bottom: 28px;
                    }

                    .section-icon {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .section-title {
                        font-size: 22px;
                    }

                    .menu-display {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 24px;
                    }

                    .meals-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 16px;
                    }
                }

                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .section {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .section-header {
                        gap: 20px;
                        margin-bottom: 32px;
                        padding-bottom: 24px;
                    }

                    .section-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                        font-size: 32px;
                    }

                    .section-title {
                        font-size: 28px;
                    }

                    .preference-toggle-section {
                        padding: 32px 40px;
                    }

                    .toggle-label {
                        font-size: 20px;
                    }

                    .toggle-btn {
                        padding: 14px 32px;
                        font-size: 15px;
                    }

                    .save-btn {
                        padding: 14px 32px;
                        font-size: 15px;
                    }
                }

                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }

                    .toggle-buttons {
                        flex-direction: column;
                        width: 100%;
                    }

                    .toggle-btn {
                        width: 100%;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {displayInitial}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{displayName}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" active={true} route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />

                <div className="main-content">
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Food Preferences</div>
                                <div className="header-name">{displayName}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {displayInitial}
                            </div>
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <div className="content-area">
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Food Preferences 🍽️</h1>
                                    <p className="hero-subtitle">Choose your daily meal preferences - Veg or Non-Veg</p>
                                </div>
                            </div>

                            {!hasExistingData && (
                                <div className="no-data-banner">
                                    <span className="no-data-icon">ℹ️</span>
                                    <div className="no-data-content">
                                        <div className="no-data-title">No Food Preferences Saved Yet</div>
                                        <div className="no-data-text">
                                            Set your preferences below and click "Save Preferences" to store your meal choices in the database.
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Current Plan</div>
                                        <div className="stat-icon">🥗</div>
                                    </div>
                                    <div className="stat-value">{stats.currentPlan}</div>
                                    <div className="stat-subtext">All meals included</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Preference</div>
                                        <div className="stat-icon">🌱</div>
                                    </div>
                                    <div className="stat-value">{stats.defaultPreference}</div>
                                    <div className="stat-subtext">Default choice</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">This Week</div>
                                        <div className="stat-icon">📊</div>
                                    </div>
                                    <div className="stat-value">{stats.totalMeals}</div>
                                    <div className="stat-subtext">Meals selected</div>
                                </div>
                                <div className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-label">Split</div>
                                        <div className="stat-icon">🍴</div>
                                    </div>
                                    <div className="stat-value">{stats.vegMeals}/{stats.nonVegMeals}</div>
                                    <div className="stat-subtext">Veg/Non-Veg</div>
                                </div>
                            </div>

                            <div className="preference-toggle-section">
                                <div className="toggle-label">Default Preference</div>
                                <div className="toggle-buttons">
                                    <button 
                                        className={`toggle-btn ${mealPreference === 'veg' ? 'active' : ''}`}
                                        onClick={() => setMealPreference('veg')}
                                    >
                                        <span>🌱</span> Vegetarian
                                    </button>
                                    <button 
                                        className={`toggle-btn ${mealPreference === 'nonveg' ? 'active' : ''}`}
                                        onClick={() => setMealPreference('nonveg')}
                                    >
                                        <span>🍖</span> Non-Vegetarian
                                    </button>
                                </div>
                            </div>

                            <div className="section">
                                <div className="section-header">
                                    <div className="section-title-group">
                                        <div className="section-icon">📋</div>
                                        <div className="section-title">
                                            {mealPreference === 'veg' ? 'Vegetarian Menu' : 'Non-Vegetarian Menu'}
                                        </div>
                                    </div>
                                </div>
                                <div className="menu-display">
                                    <div className="meal-section">
                                        <div className="meal-header">
                                            <span className="meal-icon">🌅</span>
                                            <div className="meal-title">Breakfast</div>
                                        </div>
                                        <div className="menu-items">
                                            {menuItems[mealPreference]?.breakfast?.length > 0 ? (
                                                menuItems[mealPreference].breakfast.map((item, index) => (
                                                    <div key={index} className="menu-item-card">
                                                        <div className="menu-item-icon">{item.icon}</div>
                                                        <div className="menu-item-info">
                                                            <div className="menu-item-name">{item.name}</div>
                                                            <div className="menu-item-desc">{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="empty-menu">No breakfast items available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="meal-section">
                                        <div className="meal-header">
                                            <span className="meal-icon">☀️</span>
                                            <div className="meal-title">Lunch</div>
                                        </div>
                                        <div className="menu-items">
                                            {menuItems[mealPreference]?.lunch?.length > 0 ? (
                                                menuItems[mealPreference].lunch.map((item, index) => (
                                                    <div key={index} className="menu-item-card">
                                                        <div className="menu-item-icon">{item.icon}</div>
                                                        <div className="menu-item-info">
                                                            <div className="menu-item-name">{item.name}</div>
                                                            <div className="menu-item-desc">{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="empty-menu">No lunch items available</div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="meal-section">
                                        <div className="meal-header">
                                            <span className="meal-icon">🌙</span>
                                            <div className="meal-title">Dinner</div>
                                        </div>
                                        <div className="menu-items">
                                            {menuItems[mealPreference]?.dinner?.length > 0 ? (
                                                menuItems[mealPreference].dinner.map((item, index) => (
                                                    <div key={index} className="menu-item-card">
                                                        <div className="menu-item-icon">{item.icon}</div>
                                                        <div className="menu-item-info">
                                                            <div className="menu-item-name">{item.name}</div>
                                                            <div className="menu-item-desc">{item.description}</div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="empty-menu">No dinner items available</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="section">
                                <div className="section-header">
                                    <div className="section-title-group">
                                        <div className="section-icon">📆</div>
                                        <div className="section-title">Weekly Meal Selection</div>
                                    </div>
                                    <button 
                                        className="save-btn" 
                                        onClick={handleSavePreferences}
                                        disabled={saving}
                                    >
                                        {saving ? '⏳ Saving...' : '💾 Save Preferences'}
                                    </button>
                                </div>
                                <div className="weekly-selection">
                                    {weekDays.map((day) => (
                                        <div key={day.key} className="day-selection-card">
                                            <div className="day-header">
                                                <div className="day-icon">{day.icon}</div>
                                                <div className="day-name">{day.name}</div>
                                            </div>
                                            <div className="meals-grid">
                                                <div className="meal-selection">
                                                    <div className="meal-time-label">Breakfast 🌅</div>
                                                    <div className="meal-options">
                                                        <button 
                                                            className={`meal-option-btn veg ${selectedMeals[day.key].breakfast === 'veg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'breakfast', 'veg')}
                                                        >
                                                            🌱 Veg
                                                        </button>
                                                        <button 
                                                            className={`meal-option-btn nonveg ${selectedMeals[day.key].breakfast === 'nonveg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'breakfast', 'nonveg')}
                                                        >
                                                            🍖 Non-Veg
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="meal-selection">
                                                    <div className="meal-time-label">Lunch ☀️</div>
                                                    <div className="meal-options">
                                                        <button 
                                                            className={`meal-option-btn veg ${selectedMeals[day.key].lunch === 'veg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'lunch', 'veg')}
                                                        >
                                                            🌱 Veg
                                                        </button>
                                                        <button 
                                                            className={`meal-option-btn nonveg ${selectedMeals[day.key].lunch === 'nonveg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'lunch', 'nonveg')}
                                                        >
                                                            🍖 Non-Veg
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="meal-selection">
                                                    <div className="meal-time-label">Dinner 🌙</div>
                                                    <div className="meal-options">
                                                        <button 
                                                            className={`meal-option-btn veg ${selectedMeals[day.key].dinner === 'veg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'dinner', 'veg')}
                                                        >
                                                            🌱 Veg
                                                        </button>
                                                        <button 
                                                            className={`meal-option-btn nonveg ${selectedMeals[day.key].dinner === 'nonveg' ? 'active' : ''}`}
                                                            onClick={() => handleMealToggle(day.key, 'dinner', 'nonveg')}
                                                        >
                                                            🍖 Non-Veg
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default FoodPreference;